import { ApiResult, HttpClient } from "../http.js";
import {
  CaseEconomyDto,
  CaseEconomyScope,
  CasePortfolioRowDto,
  CasePortfolioState,
  RecordStadePayload,
  SeedCaseBudgetPayload,
  StadeEntryDto,
  UpdateCaseBudgetPayload,
} from "../types/case-economy.js";

/**
 * Case economy (sagsbudget): budget vs spent vs forecast per cost type, stade, DB and the
 * portfolio. Every number is computed server-side by the one calculator.
 *
 * Permissions: reads need `budgettering.view`; budget edits `budgettering.edit`; recording stade
 * `budgettering.recordStade` (admin only by default).
 */
export class CaseEconomyApi {
  constructor(private readonly http: HttpClient) {}

  /** GET /api/projects/{id}/economy?scope=own|aggregated (default own). */
  getEconomy(projectId: string, scope: CaseEconomyScope = "own"): Promise<ApiResult<CaseEconomyDto>> {
    return this.http.get<CaseEconomyDto>(`/api/projects/${encodeURIComponent(projectId)}/economy`, {
      query: { scope },
    });
  }

  /** PUT /api/projects/{id}/budget — upsert the cost-type lines; returns the case's own economy. */
  updateBudget(projectId: string, payload: UpdateCaseBudgetPayload): Promise<ApiResult<CaseEconomyDto>> {
    return this.http.put<CaseEconomyDto>(`/api/projects/${encodeURIComponent(projectId)}/budget`, {
      body: payload,
    });
  }

  /** POST /api/projects/{id}/budget/seed — recreate the lines from a calculation or the default split. */
  seedBudget(projectId: string, payload: SeedCaseBudgetPayload): Promise<ApiResult<CaseEconomyDto>> {
    return this.http.post<CaseEconomyDto>(`/api/projects/${encodeURIComponent(projectId)}/budget/seed`, {
      body: payload,
    });
  }

  /** PUT /api/projects/{id}/economy/exclude-from-db-stats — "Tæl ikke med i DB-statistik". */
  setExcludeFromDbStats(projectId: string, exclude: boolean): Promise<ApiResult<CaseEconomyDto>> {
    return this.http.put<CaseEconomyDto>(
      `/api/projects/${encodeURIComponent(projectId)}/economy/exclude-from-db-stats`,
      { body: { exclude } },
    );
  }

  /** GET /api/projects/{id}/stade — the stade history, newest first. */
  listStade(projectId: string): Promise<ApiResult<StadeEntryDto[]>> {
    return this.http.get<StadeEntryDto[]>(`/api/projects/${encodeURIComponent(projectId)}/stade`);
  }

  /** POST /api/projects/{id}/stade — record the faktisk stade (needs `budgettering.recordStade`). */
  recordStade(projectId: string, payload: RecordStadePayload): Promise<ApiResult<StadeEntryDto>> {
    return this.http.post<StadeEntryDto>(`/api/projects/${encodeURIComponent(projectId)}/stade`, {
      body: payload,
    });
  }

  /**
   * GET /api/economy/cases?state=active|closed (default active). One row per case, aggregated over
   * its delprojekter; callers without `budgettering.viewAllBudgets` only get their own cases.
   */
  listCases(state: CasePortfolioState = "active"): Promise<ApiResult<CasePortfolioRowDto[]>> {
    return this.http.get<CasePortfolioRowDto[]>("/api/economy/cases", { query: { state } });
  }
}
